// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * Copy, cut and paste on the site plan.
 *
 * Six porta-potties, four dumpsters, the same trailer on three phases of the
 * same job — a logistics plan is mostly repetition, and placing each one again
 * from the palette and resizing it to match is work the computer should be
 * doing. The keys people already press should do it.
 *
 * A pasted element is a deep copy with a new id, nudged clear of the original
 * so it is visibly a second thing rather than appearing to have done nothing.
 * Paste again and it steps further, the way every drawing tool does, so a row
 * of them can be laid down without moving each one first.
 */

import type { SiteLogistics } from "./logistics";

export type ElementKind = "icon" | "line" | "area" | "circle" | "text" | "dimension";

export interface Clip { kind: ElementKind; data: any }

/** How far a pasted copy lands from the thing it was copied from, in percent. */
export const PASTE_NUDGE = 2.5;

/** Which list each kind lives in. Exported because every operation that
 *  reaches an element by id needs the same walk, and two copies of this map
 *  would drift the first time a kind is added. */
export const LIST: Record<ElementKind, keyof SiteLogistics> = {
  icon: "icons", line: "lines", area: "areas", circle: "circles", text: "texts",
  dimension: "dimensions",
};

const listOf = (sl: SiteLogistics, kind: ElementKind): any[] =>
  ((sl as any)[LIST[kind]] as any[]) || [];

/** The element with this id, whichever kind it turns out to be. */
export function findElement(sl: SiteLogistics, id: string | null): { kind: ElementKind; el: any } | null {
  if (!sl || !id) return null;
  for (const kind of Object.keys(LIST) as ElementKind[]) {
    const el = listOf(sl, kind).find((e) => e?.id === id);
    if (el) return { kind, el };
  }
  return null;
}

/**
 * What to put on the clipboard.
 *
 * A deep copy, so editing or deleting the original afterwards cannot reach
 * through and change what gets pasted.
 */
export function copyElement(sl: SiteLogistics, id: string | null): Clip | null {
  const found = findElement(sl, id);
  if (!found) return null;
  return { kind: found.kind, data: JSON.parse(JSON.stringify(found.el)) };
}

/** The plan without that element. */
export function removeElement(sl: SiteLogistics, id: string | null): SiteLogistics {
  const found = findElement(sl, id);
  if (!found) return sl;
  const key = LIST[found.kind];
  return { ...sl, [key]: listOf(sl, found.kind).filter((e) => e.id !== id) } as SiteLogistics;
}

/** Everything that says where an element is, moved by the same amount. */
function nudged(kind: ElementKind, data: any, by: number): any {
  const el = JSON.parse(JSON.stringify(data));
  if (kind === "line" || kind === "area") {
    el.points = (el.points || []).map((p: any) => ({ ...p, x: p.x + by, y: p.y + by }));
  } else if (kind === "circle") {
    el.cx += by; el.cy += by;
  } else if (kind === "dimension") {
    // Both ends move together, or the copy measures something else.
    el.a = { x: el.a.x + by, y: el.a.y + by };
    el.b = { x: el.b.x + by, y: el.b.y + by };
  } else {
    el.x += by; el.y += by;
  }
  return el;
}

/**
 * The plan with a copy of the clipboard added, and the new element's id.
 *
 * `step` counts how many times this clip has been pasted, so a run of pastes
 * walks away from the original instead of stacking in one place.
 */
export function pasteElement(
  sl: SiteLogistics, clip: Clip | null, newId: string, step = 1,
): { sl: SiteLogistics; id: string | null } {
  if (!sl || !clip || !LIST[clip.kind]) return { sl, id: null };
  const el = nudged(clip.kind, clip.data, PASTE_NUDGE * Math.max(1, step));
  el.id = newId;
  // A copy arrives unlocked. Locking says "leave this one alone", which is
  // about the thing on the drawing, not about the shape of it -- and a pasted
  // copy you cannot move is a copy you cannot place.
  delete el.locked;
  const key = LIST[clip.kind];
  return {
    sl: { ...sl, [key]: [...listOf(sl, clip.kind), el] } as SiteLogistics,
    id: newId,
  };
}

/**
 * An element shifted by the arrow keys.
 *
 * Nudging with the arrows is how anything on a canvas gets lined up precisely,
 * and dragging cannot do it: the pointer moves in whole pixels and the hand
 * shakes. Same mover as paste, so a line and a circle travel the same way.
 */
export function nudgeElement(sl: SiteLogistics, id: string | null, dx: number, dy: number): SiteLogistics {
  const found = findElement(sl, id);
  if (!found || (dx === 0 && dy === 0)) return sl;
  const { kind, el } = found;
  const moved = JSON.parse(JSON.stringify(el));
  if (kind === "line" || kind === "area") {
    moved.points = (moved.points || []).map((p: any) => ({ ...p, x: p.x + dx, y: p.y + dy }));
  } else if (kind === "circle") {
    moved.cx += dx; moved.cy += dy;
  } else if (kind === "dimension") {
    moved.a = { x: moved.a.x + dx, y: moved.a.y + dy };
    moved.b = { x: moved.b.x + dx, y: moved.b.y + dy };
  } else {
    moved.x += dx; moved.y += dy;
  }
  const key = LIST[kind];
  return { ...sl, [key]: listOf(sl, kind).map((e) => (e.id === id ? moved : e)) } as SiteLogistics;
}

/** How far one arrow press moves something, and one with Shift held. */
export const NUDGE_STEP = 0.4;
export const NUDGE_STEP_COARSE = 2;

/** A label that says what will be pasted, for a menu item. */
export function describeClip(clip: Clip | null): string {
  if (!clip) return "";
  const name = clip.data?.label || clip.data?.text || "";
  const kind = clip.kind === "icon" ? "item" : clip.kind;
  return name ? `${kind} “${String(name).slice(0, 24)}”` : kind;
}
